import React from 'react';
import { Modal as AntdModal } from 'antd';
import { FiX } from 'react-icons/fi';

/**
 * Themed wrapper around antd v3's Modal. Keeps antd's portal/focus-trap/
 * keyboard-escape behavior; renders our own header + close + body padding
 * for a consistent design-system look.
 *
 * The CSS overrides in src/styles/theme.css already restyle `.ant-modal-*`
 * classes, so even uses of `<antd.Modal>` directly look right; this wrapper
 * adds the opinionated header layout (title + description + close).
 */
export default function Modal({
  visible,
  onCancel,
  onClose,
  title,
  description,
  children,
  footer,
  width = 480,
  centered = true,
  maskClosable = true,
  className = '',
  bodyClassName = '',
}) {
  const handleClose = onCancel || onClose;

  return (
    <AntdModal
      visible={visible}
      onCancel={handleClose}
      footer={null}
      title={null}
      closable={false}
      width={width}
      centered={centered}
      maskClosable={maskClosable}
      bodyStyle={{ padding: 0 }}
      className={`nubod-modal ${className}`}
    >
      <div className="bg-surface text-fg rounded-xl overflow-hidden">
        {(title || handleClose) && (
          <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3 border-b border-border-soft">
            <div className="min-w-0 flex-1">
              {title ? (
                <h2 className="text-lg font-semibold leading-tight text-fg">
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p className="mt-1 text-sm text-fg-muted">{description}</p>
              ) : null}
            </div>
            {handleClose ? (
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                className="shrink-0 -mr-1 -mt-1 p-1 rounded text-fg-muted hover:text-fg hover:bg-soft transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            ) : null}
          </div>
        )}
        <div className={`px-5 py-4 ${bodyClassName}`}>{children}</div>
        {footer ? (
          <div className="px-5 py-3 border-t border-border-soft bg-elev/60">
            {footer}
          </div>
        ) : null}
      </div>
    </AntdModal>
  );
}
